"use client";

import React from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useOnboarding } from "@/onboarding/OnboardingContext";
import { useTenancy } from "@/contexts/TenancyContext";
import { deiterateAPI } from "@/js/external-js/variableModules/utils/deiterateAPI.util";
import { getAssetsByType, createAsset } from "@/services/assetsService";
import { toast } from "sonner";
import { DuckLoadingScreen } from "@/components/DuckLoadingScreen";
import { Check } from "lucide-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@awesome.me/kit-269fbef2e2/icons/duotone/solid";
import { STATIC_SUPPLIERS } from "@/data/staticSuppliers";


export default function ModalSupplierSelectionExample() {
  const [open, setOpen] = React.useState(true);
  const { setStatus, openStep, closeActive, goBack, stepData, setStepData } = useOnboarding();
  
  // Load saved state from stepData
  const savedData = stepData["add_suppliers"]?.data;
  const [query, setQuery] = React.useState(savedData?.query || "");
  const [selected, setSelected] = React.useState<string[]>(savedData?.selected || ["de.iterate"]);
  const { enabledModules } = useTenancy();
  const [loading, setLoading] = React.useState(false);
  const [aiError, setAiError] = React.useState<string | null>(null);
  const [aiMsg, setAiMsg] = React.useState<string | null>(null);
  const [items, setItems] = React.useState<Array<{ id: string; name: string; count?: number; domains?: string[] }>>([]);
  const [listLoading, setListLoading] = React.useState(false);
  const [domain, setDomain] = React.useState(savedData?.domain || "");
  const [adding, setAdding] = React.useState(false);
  const [addError, setAddError] = React.useState<string | null>(null);
  const [existingSuppliers, setExistingSuppliers] = React.useState<Set<string>>(new Set());

  React.useEffect(() => {
    // Load existing suppliers to check for duplicates
    getAssetsByType('supplier').then(assets => {
      const names = new Set(assets.map((a: any) => (a.assetName || a.name || a.assetDesc || '').toLowerCase()));
      setExistingSuppliers(names);
    }).catch(console.error);
  }, []);

  // Cache all suppliers for client-side filtering - restore custom suppliers from saved state
  const [allSuppliers, setAllSuppliers] = React.useState<Array<{ id: string; name: string; count?: number; domains?: string[] }>>(() => {
    const customSuppliers = savedData?.customSuppliers || [];
    return [...customSuppliers, ...STATIC_SUPPLIERS];
  });
  
  // Initialize items with static suppliers
  React.useEffect(() => {
    setItems(STATIC_SUPPLIERS.slice(0, 12));
  }, []);

  // Persist query changes
  React.useEffect(() => {
    setStepData("add_suppliers", { query });
  }, [query, setStepData]);
  
  // Persist domain changes
  React.useEffect(() => {
    setStepData("add_suppliers", { domain });
  }, [domain, setStepData]);
  
  // Filter suppliers client-side when query changes (supports contains search)
  // Custom and selected suppliers are always shown first
  React.useEffect(() => {
    const q = query.trim().toLowerCase();
    
    // Separate custom/selected suppliers from regular ones
    const customSuppliers = allSuppliers.filter((item: any) => item.isCustom || selected.includes(item.name));
    const regularSuppliers = allSuppliers.filter((item: any) => !item.isCustom && !selected.includes(item.name));
    
    if (!q) {
      // No query - show custom/selected first, then top regular suppliers
      const regularSlice = regularSuppliers.slice(0, Math.max(0, 12 - customSuppliers.length));
      setItems([...customSuppliers, ...regularSlice]);
    } else {
      // Filter by contains (not just prefix), but always include matching custom/selected
      const filteredCustom = customSuppliers.filter(item => item.name?.toLowerCase().includes(q));
      const filteredRegular = regularSuppliers
        .filter(item => item.name?.toLowerCase().includes(q))
        .slice(0, Math.max(0, 12 - filteredCustom.length));
      setItems([...filteredCustom, ...filteredRegular]);
    }
  }, [query, allSuppliers, selected]);

  const canAdd = query.trim().length > 1 && !items.some(i => i.name?.toLowerCase() === query.trim().toLowerCase());
  const toggle = (name: string) => {
    setSelected(prev => {
      const newSelected = prev.includes(name) ? prev.filter(x => x !== name) : [...prev, name];
      // Persist selection changes
      setStepData("add_suppliers", { selected: newSelected });
      return newSelected;
    });
  };

  const addSupplier = async () => {
    if (!canAdd) return;
    const nameToAdd = query.trim();
    
    // Check duplicates against existing assets
    if (existingSuppliers.has(nameToAdd.toLowerCase())) {
        toast.error(`Supplier "${nameToAdd}" already exists in your assets.`);
        // Optionally select it if we can map it, but for now just warn
        return;
    }

    setAdding(true);
    setAddError(null);
    try {
      // Get the actual tenancy owner ID instead of using placeholder
      const tenancyOwner = window.activeTenancy?.tenancyOwner || '';
      const domainValue = domain.trim().toLowerCase();
      
      const newAsset: any = {
        assetDesc: nameToAdd,
        assetCat: 'Outsourced Services', // Default category for suppliers
        assetOwner: tenancyOwner, // Use actual tenancy owner ID
        domain: domainValue,
        comments: `Added via Onboarding`,
        status: 'Active' as const,
        // If domain is provided, enable vendor monitoring and set vendor website
        ...(domainValue && {
          vendorManagementNeeded: 'Yes',
          vendorWebsite: domainValue.startsWith('http') ? domainValue : `https://${domainValue}`
        })
      };

      const created = await createAsset(newAsset);
      
      if (created && created.id) {
          const newItem = {
            id: created.id,
            name: nameToAdd,
            count: 0,
            domains: (domain.trim() ? [domain.trim().toLowerCase()] : []),
            isCustom: true // Mark as custom so we can always show it
          };
          
          // Add to allSuppliers so it persists through filtering
          const updatedSuppliers = [newItem, ...allSuppliers];
          setAllSuppliers(updatedSuppliers);
          setItems(prev => [newItem, ...prev]);
          const updatedSelected = Array.from(new Set([...selected, newItem.name]));
          setSelected(updatedSelected);
          setExistingSuppliers(prev => new Set(prev).add(nameToAdd.toLowerCase()));
          setDomain("");
          setQuery(""); // Clear query on success
          
          // Persist custom suppliers and selection
          const customSuppliers = updatedSuppliers.filter((s: any) => s.isCustom);
          setStepData("add_suppliers", { 
            customSuppliers,
            selected: updatedSelected,
            query: "",
            domain: ""
          });
          
          toast.success("Supplier added successfully");
      } else {
          throw new Error("Failed to create asset");
      }
    } catch (e) {
      console.error(e);
      setAddError('Failed to add supplier');
    } finally {
      setAdding(false);
    }
  };

  const runAISuggestions = async () => {
    setLoading(true);
    setAiError(null);
    setAiMsg(null);
    try {
      const ctxRaw = typeof window !== "undefined" ? localStorage.getItem("onboardingAI.companyContext") : null;
      const ctx = ctxRaw ? JSON.parse(ctxRaw) : {};
      const companyId = (window as any).companyID || localStorage.getItem("companyID") || "";
      const tenancyOwner = window.activeTenancy?.tenancyOwner || '';
      
      // Create assets for all selected suppliers that don't already exist
      console.log('[SupplierSelection] Creating assets for selected suppliers:', selected);
      const suppliersToCreate = selected.filter(name => !existingSuppliers.has(name.toLowerCase()));
      
      for (const supplierName of suppliersToCreate) {
        try {
          // Find the supplier in our items list to get domain info
          const supplierItem = allSuppliers.find(s => s.name === supplierName);
          const domainValue = supplierItem?.domains?.[0] || '';
          
          const newAsset: any = {
            assetDesc: supplierName,
            assetCat: 'Outsourced Services',
            assetOwner: tenancyOwner,
            domain: domainValue,
            comments: 'Added via Onboarding',
            status: 'Active' as const,
            ...(domainValue && {
              vendorManagementNeeded: 'Yes',
              vendorWebsite: domainValue.startsWith('http') ? domainValue : `https://${domainValue}`
            })
          };
          
          await createAsset(newAsset);
          console.log(`[SupplierSelection] Created asset for: ${supplierName}`);
        } catch (err) {
          console.error(`[SupplierSelection] Failed to create asset for ${supplierName}:`, err);
          // Continue with other suppliers even if one fails
        }
      }
      
      // Build the request body with enabled frameworks for control matching
      const body = {
        customerId: companyId,
        companyName: (window as any).companyName || ctx.companyName || (ctx.domain ? String(ctx.domain).split(".")[0] : "Company"),
        domain: ctx.domain || "",
        teamSize: ctx.teamSize,
        officesCount: ctx.officesCount,
        include_sections: ["data_labels", "data_pools", "processes"],
        suppliers: selected.map((name: string, i: number) => ({ id: `SUP-${i}`, name })),
        // Include enabled frameworks/modules for LLM-based control matching
        // This triggers suggestions for relevant controls from these frameworks for each process
        framework_ids: enabledModules.length > 0 ? enabledModules : undefined
      } as any;

      console.log('[SupplierSelection] Calling suggestions API with frameworks:', enabledModules);

      const res: any = await deiterateAPI.POST("/deiterateInternalAPI/ai/api/onboarding-ai/suppliers/generate", {
        body: JSON.stringify(body)
      });
      try { localStorage.setItem("onboardingAI.lastResponse", JSON.stringify(res)); } catch {}
      setAiMsg("Suggestions generated.");
      toast.success(`${suppliersToCreate.length} suppliers added to your register`);
      setStatus("add_suppliers","done");
      setOpen(false);
      openStep("add_processes");
    } catch (e) {
      console.error('[SupplierSelection] Generation failed:', e);
      setAiError("Failed to generate suggestions. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <Button onClick={() => setOpen(true)}>Open Supplier Selection</Button>
      <Dialog open={open} onOpenChange={(v) => { 
        // Prevent closing while loading
        if (loading) return;
        setOpen(v); 
        if (!v) closeActive(); 
      }}>
        <DialogContent 
          className="max-w-[95vw] sm:max-w-[90vw] lg:max-w-7xl h-screen bg-transparent shadow-none border-0 p-0 flex items-center justify-center"
          onInteractOutside={(e) => { if (loading) e.preventDefault(); }}
          onEscapeKeyDown={(e) => { if (loading) e.preventDefault(); }}
        >
          <div className="bg-card/95 backdrop-blur-xl rounded-xl w-full max-h-[90vh] overflow-hidden shadow-2xl border border-white/10 flex flex-col relative">
            {/* Close button */}
            <button
              onClick={() => { if (!loading) { setOpen(false); closeActive(); } }}
              className="absolute right-4 top-4 z-50 h-8 w-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
              aria-label="Close"
              disabled={loading}
            >
              <FontAwesomeIcon icon={faXmark} className="h-4 w-4 text-muted-foreground" />
            </button>
            
            {/* Duck Loading Screen Overlay */}
            {loading && (
              <div className="absolute inset-0 z-50 rounded-xl overflow-hidden">
                <DuckLoadingScreen
                  variant="overlay"
                  title="Saving Suppliers"
                  text="We are saving your supplier list, and preparing a list of suggested business processes that may be in use relating to your compliance program."
                  duckFacts={[
                    "Quack! Analysing your supplier ecosystem...",
                    "Mapping data flows between vendors...",
                    "Identifying relevant data labels...",
                    "Suggesting process improvements...",
                    "Almost there! Finalising recommendations..."
                  ]}
                />
              </div>
            )}
            
            <div className="p-4 sm:p-6 pb-4 border-b border-white/10 bg-linear-to-b from-primary/5 to-transparent">
                <h2 className="text-2xl sm:text-3xl font-bold text-foreground">Select Your Suppliers</h2>
                <p className="mt-2 text-sm sm:text-base text-muted-foreground">Please select any suppliers you use from the list below and click "Next" to continue.</p>
              </div>

              <div className="p-4 sm:p-6 flex-1 overflow-y-auto space-y-4">
                <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
                  <Input placeholder="Search suppliers..." value={query} onChange={(e) => setQuery(e.target.value)} className="bg-white/10 border-white/20 sm:max-w-sm" />
                  <div className="text-xs text-muted-foreground">{selected.length} selected</div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3">
                  {canAdd && (
                    <div className="flex flex-col gap-2 rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm p-4">
                      <div className="text-foreground font-medium">Add "{query.trim()}"</div>
                      <div className="text-xs text-muted-foreground">Optional domain</div>
                      <div className="flex gap-2">
                        <Input placeholder="example.com" value={domain} onChange={(e) => setDomain(e.target.value)} className="bg-white/10 border-white/20" />
                        <Button onClick={addSupplier} disabled={adding}>{adding ? 'Adding…' : 'Add'}</Button>
                      </div>
                      {addError && <div className="text-xs text-destructive">{addError}</div>}
                    </div>
                  )}
                  {items.map(item => {
                    const isSelected = selected.includes(item.name);
                    return (
                      <div
                        key={item.id || item.name}
                        onClick={() => toggle(item.name)}
                        style={{
                          background: isSelected ? 'linear-gradient(135deg, rgba(var(--primary-rgb), 0.15), rgba(var(--primary-rgb), 0.05))' : '',
                          backdropFilter: 'blur(8px)'
                        }}
                        className={`relative overflow-hidden rounded-xl p-4 cursor-pointer transition-all duration-300 transform hover:scale-[1.02] ${
                          isSelected
                            ? 'border-2 border-primary shadow-lg'
                            : 'bg-white/5 hover:bg-white/10 dark:bg-gray-800/30 dark:hover:bg-gray-800/50 border border-white/10 shadow-sm hover:shadow-md'
                        }`}
                      >
                        {/* Light accent when selected */}
                        {isSelected && (
                          <div className="absolute inset-0 bg-gradient-to-tr from-primary/5 via-transparent to-primary/10 pointer-events-none" />
                        )}
                        
                        {/* Selection indicator */}
                        {isSelected && (
                          <div className="absolute top-3 right-3 h-6 w-6 rounded-full bg-primary flex items-center justify-center shadow-sm z-20">
                            <Check className="h-3 w-3 text-primary-foreground" />
                          </div>
                        )}
                        
                        <div className="relative z-10">
                          <div className="text-foreground font-medium">{item.name}</div>
                          <div className="text-xs text-muted-foreground mt-1">Suggested based on vendor popularity</div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* {selected.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {selected.map(s => (
                      <Badge key={s} variant="outline" className="text-xs">{s}</Badge>
                    ))}
                  </div>
                )} */}
                {aiError && (
                  <div className="text-xs text-destructive">{aiError}</div>
                )}
                {aiMsg && !aiError && (
                  <div className="text-xs text-muted-foreground">{aiMsg}</div>
                )}
              </div>

            <div className="border-t border-white/10 px-4 sm:px-6 py-4 bg-linear-to-t from-primary/5 to-transparent flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setOpen(false); goBack(); }}>Back</Button>
              <Button onClick={runAISuggestions} disabled={loading || selected.length === 0}>{loading ? "Generating…" : "Next"}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
